export function formatNumber(
  value: number | null | undefined,
  locale: string,
): string {
  if (value == null || !Number.isFinite(value)) {
    return '0';
  }

  return new Intl.NumberFormat(locale).format(value);
}

export function formatCost(
  value: number | null | undefined,
  locale: string,
): string {
  if (value == null || !Number.isFinite(value)) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 2,
    }).format(0);
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatDuration(
  value: number | null | undefined,
  locale: string,
): string {
  if (value == null || !Number.isFinite(value) || value < 0) {
    return formatNumber(0, locale) + ' ms';
  }

  if (value < 1000) {
    return `${formatNumber(Math.round(value), locale)} ms`;
  }

  const seconds = value / 1000;
  if (seconds < 60) {
    return `${new Intl.NumberFormat(locale, {
      maximumFractionDigits: 1,
    }).format(seconds)} s`;
  }

  const minutes = seconds / 60;
  if (minutes < 60) {
    return `${new Intl.NumberFormat(locale, {
      maximumFractionDigits: 1,
    }).format(minutes)} min`;
  }

  const hours = minutes / 60;
  return `${new Intl.NumberFormat(locale, {
    maximumFractionDigits: 1,
  }).format(hours)} h`;
}

/**
 * Parses Java-style ISO-8601 durations such as `PT35.884034S` or `PT1H2M3.5S`.
 * Returns milliseconds, or `null` when the value cannot be parsed.
 */
export function parseIso8601DurationToMillis(
  value: string | null | undefined,
): number | null {
  if (value == null) {
    return null;
  }

  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }

  const match =
    /^(-)?P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/i.exec(
      trimmed,
    );
  if (!match) {
    return null;
  }

  const sign = match[1] ? -1 : 1;
  const days = Number(match[2] ?? 0);
  const hours = Number(match[3] ?? 0);
  const minutes = Number(match[4] ?? 0);
  const seconds = Number(match[5] ?? 0);

  if (![days, hours, minutes, seconds].every(Number.isFinite)) {
    return null;
  }

  const millis = (((days * 24 + hours) * 60 + minutes) * 60 + seconds) * 1000;
  return sign * millis;
}

export function formatIso8601Duration(
  value: string | null | undefined,
  locale: string,
): string | null {
  const millis = parseIso8601DurationToMillis(value);
  if (millis == null) {
    return null;
  }
  return formatDuration(millis, locale);
}

export function formatBytes(
  value: number | null | undefined,
  locale: string,
): string {
  if (value == null || !Number.isFinite(value) || value < 0) {
    return `0 B`;
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'] as const;
  let size = value;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${new Intl.NumberFormat(locale, {
    maximumFractionDigits: unitIndex === 0 ? 0 : 1,
  }).format(size)} ${units[unitIndex]}`;
}

/** Always 24-hour clock (00–23). No AM/PM regardless of locale. */
const TIMESTAMP_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
};

/** Time of day only — always 24-hour clock (00–23). No AM/PM. */
const TIME_OF_DAY_FORMAT: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
};

export function formatTimestamp(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, TIMESTAMP_FORMAT).format(
    new Date(value),
  );
}

export function formatTimeOfDay(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, TIME_OF_DAY_FORMAT).format(
    new Date(value),
  );
}

export function formatFileType(fileName: string, mimeType: string): string {
  const extension = fileName.includes('.')
    ? fileName.split('.').pop()?.trim()
    : undefined;

  if (extension && extension.length > 0 && extension.length <= 8) {
    return extension.toUpperCase();
  }

  const subtype = mimeType.split('/')[1]?.trim();
  if (subtype) {
    return subtype.toUpperCase();
  }

  return mimeType;
}
